// O gráfico "dia a dia" dos sinais vitais — a geometria, sem desenho.
//
// Tela (SVG) e PDF (canvas do pdfmake) desenham a mesma coisa a partir daqui:
// para cada dia, um traço do menor ao maior valor medido e um ponto na média, e
// a faixa usual de adulto em verde atrás. A escala inclui a faixa: sem ela, um
// dia que variou pouco ocuparia a caixa inteira e pareceria um desastre.
import { FAIXA_USUAL_SINAL, type TipoLeitura } from '@medicov1/shared';
import type { Sinal } from './relatorioTipos';

/** Os sinais que entram no gráfico, nesta ordem. Os de balanço e dieta ficam de fora. */
export const SINAIS_DO_GRAFICO: TipoLeitura[] = [
  'pa_sistolica', 'pa_diastolica', 'frequencia_cardiaca', 'saturacao', 'temperatura', 'glicemia', 'frequencia_respiratoria',
];

export type DiaDoSinal = NonNullable<Sinal['porDia']>[number];

export interface GeometriaDoSinal {
  barras: { x: number; y1: number; y2: number }[];
  pontos: { x: number; y: number }[];
  faixa: { y: number; h: number } | null;
}

/** Espaça pelo CALENDÁRIO: dias sem medida viram vão, não somem. */
export function geometriaDoSinal(
  dias: DiaDoSinal[],
  faixaUsual: { min: number; max: number } | null,
  largura: number,
  altura: number,
): GeometriaDoSinal {
  if (dias.length === 0) return { barras: [], pontos: [], faixa: null };
  let min = Math.min(...dias.map((d) => d.menor), ...(faixaUsual ? [faixaUsual.min] : []));
  let max = Math.max(...dias.map((d) => d.maior), ...(faixaUsual ? [faixaUsual.max] : []));
  if (max === min) { max += 1; min -= 1; }
  const folga = (max - min) * 0.08;
  min -= folga; max += folga;
  const y = (v: number) => altura - ((v - min) / (max - min)) * altura;

  const numero = (dia: string) => Date.UTC(+dia.slice(0, 4), +dia.slice(5, 7) - 1, +dia.slice(8, 10)) / 86_400_000;
  const d0 = numero(dias[0]!.dia);
  const d1 = numero(dias[dias.length - 1]!.dia);
  const margem = 3; // o traço do primeiro e do último dia não cola na borda
  const x = (dia: string) => (d1 === d0 ? largura / 2 : margem + ((numero(dia) - d0) / (d1 - d0)) * (largura - 2 * margem));

  const faixa = faixaUsual
    ? (() => {
      const topo = Math.max(0, y(faixaUsual.max));
      const base = Math.min(altura, y(faixaUsual.min));
      return base > topo ? { y: topo, h: base - topo } : null;
    })()
    : null;

  return {
    barras: dias.map((d) => ({ x: x(d.dia), y1: y(d.maior), y2: y(d.menor) })),
    pontos: dias.map((d) => ({ x: x(d.dia), y: y(d.media) })),
    faixa,
  };
}

/** Os sinais que têm pelo menos dois dias para desenhar, na ordem do gráfico. */
export function sinaisComGrafico(sinais: Sinal[]): Sinal[] {
  return SINAIS_DO_GRAFICO
    .map((t) => sinais.find((s) => s.tipo === t))
    .filter((s): s is Sinal => Boolean(s && s.porDia && s.porDia.length >= 2));
}

export const faixaDoSinal = (tipo: string) => FAIXA_USUAL_SINAL[tipo as TipoLeitura] ?? null;
